<script lang="ts" setup>
import '../../../css/choice.css';
import { popAlert, confirmDialog, ModalStyles } from "../../lib/Notification";
import { onMounted, onBeforeMount, reactive, ref, computed, nextTick } from 'vue';
import { ChoiceApi, LoadingSection } from '../../LibMain';
import PatientHeader from '../../components/Choice/Patient/PatientHeader.vue'
import ChoiceTable from "../../components/ChoiceTable.vue";
import { ICol, IRowSelection, RenderModeType } from '../../lib/ChoiceTableHelpers';
import ResizeMonitor from '../../components/ResizeMonitor.vue'
import { openWindowFromVue } from '../../lib/Models/CommonItemsModel';
import GeneralDialog from '../../components/GeneralDialog.vue';
import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
import { claimEntryScreenUrl, loggingURL, mergeIntakeURL } from '../../lib/OfficeMdUrls';
import { AlignmentType, TableRowType, TableRowHighlight, ButtonDisplaySize, ButtonDisplayStyle, ModalSize, LayoutOrientation, DateDisplayFormat, IconTypes, DisplayStyle, HoverCursorTypes, ToastDisplayStyles } from '../../Enums/UiEnums'
import { PortalType } from '../../Enums/ChoiceEnums';
import { IntakePatientInfo, contactItem, contactLinkType, IntakeItem, IntakeOfficeCodeResponse, IntakeOfficeCodePostRequest, IntakeGetOfficeCodeRequest, IntakeAppointment, IntakeAssignmentActive, IntakeTypeList, IntakeAppointmentDescription, IntakeSaveRequest, IntakeAssignmentHistoric, IntakeDeleteRequest, IntakeLinkCreateRequest, IntakeRefreshActive } from '../../lib/Models/IntakeModel';
import ModalDialog from '../../components/ModalDialog.vue'
import ActionButtonV2 from '../../components/ActionButtonV2.vue';
import { ActionButtonStyle } from '../../lib/Types/Types';
import RadioButtonInput from '../../components/RadioButtonInput.vue';
import vSelect from 'vue-select';
import Combo from "../../components/Combo.vue";
import * as DateUtils from '../../lib/DateUtils';
import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
import Icon from '../../components/Icon.vue';
import TextInput from '../../components/TextInput.vue';
import LoadingSectionV2 from '../../components/LoadingSectionV2.vue';
import ProgressBarIcon from '../../components/ProgressBarIcon.vue';
import ToastContainer from "../../components/Messages/ToastContainer.vue"
import { ToastItem } from '../../LibMain';
import AlertSection from '../../components/Messages/AlertSection.vue';
import { CssLayoutUnits, CssLength } from '../../lib/CssHelpers';

enum endPointState {
    loading = 0,
    success = 1,
    error = 2
}
const toastsArray = ref<ToastItem[]>([]);
function addToast(displayStyle: ToastDisplayStyles) {
    const newToast = new ToastItem("Sample Toast", `Some simple/short message @ ${new Date().toTimeString()}`, displayStyle);

    // Hide the action link for success messages
    if (DisplayStyle.Success === displayStyle)
        newToast.showActionLink = false;

    toastsArray.value.push(newToast);
}
enum AssignmentType {
    Active = 'A',
    Historical = 'H'
}

enum saveIntakeAssignType {
    Appointment = 1,
    DueDate = 2
}

const AssnTable = ref();

const compData = reactive<{
    patientId?: number,
    patientData?: IntakePatientInfo,
    intakeItemList?: IntakeItem[],
    intakeAppointmentList?: IntakeAppointment[],
    activeIntakeAssignmentList?: IntakeAssignmentActive[],
    historicIntakeAssignmentList?: IntakeAssignmentHistoric[],
    showSaveIntakeModal: boolean,
    showSendIntakeModal: boolean,
    showOfficeCodeDialog: boolean,
    showAssignmentDetailsDialog: boolean,
    disableSaveIntakeCombo: boolean,
    assignmentDisplayType: AssignmentType,
    saveIntakeAssignId?: number,
    saveIntakeAssignType: saveIntakeAssignType
    saveIntakeApptId?: number,
    saveIntakeSetupId?: number,
    saveIntakeDueDate?: DateUtils.NoTzDate,
    intakeAppointmentDescription?: IntakeAppointmentDescription[],
    selectedAssignment?: IntakeAssignmentActive | IntakeAssignmentHistoric,
    assnTableHeight: number,
    toContactList?: contactItem[],
    sendIntakeToContactRecord?: contactItem,
    sendIntaketoContactType?: string,
    officeCodeRecord?: IntakeOfficeCodeResponse,
    loadingPostOfficeCode: endPointState
}>({
    loadingPostOfficeCode: endPointState.success,
    assnTableHeight: 0,
    showSaveIntakeModal: false,
    showSendIntakeModal: false,
    showOfficeCodeDialog: false,
    showAssignmentDetailsDialog: false,
    disableSaveIntakeCombo: false,
    assignmentDisplayType: AssignmentType.Active,
    saveIntakeAssignType: saveIntakeAssignType.Appointment
});

const dialogWidth = new CssLength(300, CssLayoutUnits.px);

function windowResize() {
    compData.assnTableHeight = window.innerHeight * 0.4;
}
const props = withDefaults(defineProps<{
    patientid: number,
    patientdata: IntakePatientInfo,
    activeintakedata: IntakeRefreshActive
}>(), {
    patientdata: undefined
});

onBeforeMount(async () => {
    compData.patientId = props.patientid
    compData.patientData = props.patientdata;
    const resp = props.activeintakedata;
    compData.intakeItemList = resp.intakeItemList
    compData.intakeAppointmentList = resp.intakeAppointmentList
    compData.activeIntakeAssignmentList = resp.intakeAssignmentList
    buildContactList();
    windowResize();
})

function buildContactList() {
    compData.toContactList = [];
    if (compData.patientData?.consentEmail) {
        compData.toContactList.push({ contactDetails: compData.patientData?.consentEmail, type: contactLinkType.consentEmail });
    }
    if (compData.patientData?.consentText) {
        compData.toContactList.push({ contactDetails: compData.patientData?.consentText, type: contactLinkType.consentText });
    }
    if (compData.patientData?.email && compData.patientData?.email !== compData.patientData?.consentEmail) {
        compData.toContactList.push({ contactDetails: compData.patientData?.email, type: contactLinkType.email });
    }
    if (compData.patientData?.phoneHome && compData.patientData?.phoneHome !== compData.patientData?.consentText) {
        compData.toContactList.push({ contactDetails: compData.patientData?.phoneHome, type: contactLinkType.phoneHome });
    }
    if (compData.patientData?.phoneWork && compData.patientData?.phoneWork !== compData.patientData?.consentText) {
        compData.toContactList.push({ contactDetails: compData.patientData?.phoneWork, type: contactLinkType.phoneWork });
    }
    if (compData.patientData?.phoneOther && compData.patientData?.phoneOther !== compData.patientData?.consentText) {
        compData.toContactList.push({ contactDetails: compData.patientData?.phoneOther, type: contactLinkType.phoneOther });
    }
}

async function onChangeAssignment(): Promise<void> {
    clearSelection();
    refreshData();
    windowResize();
}

function activeFilter(isActive: boolean): string {
    if (isActive) {
        return 'Active'
    }
    return 'InActive'
}

const IntakeType = computed<IntakeTypeList[] | undefined>(() =>
    compData.intakeItemList?.map(o => ({
        ...o,
        status: activeFilter(o.active)
    }))
);

const validIntakeSave = computed<boolean>(() => {
    if (!compData.saveIntakeSetupId) {
        return false;
    }
    if (compData.saveIntakeAssignType === saveIntakeAssignType.Appointment && !compData.saveIntakeApptId) {
        return false;
    }
    if (compData.saveIntakeAssignType === saveIntakeAssignType.DueDate && !compData.saveIntakeDueDate) {
        return false;
    }
    return true;
});

async function refreshData() {

    await refresh();
    if (compData.selectedAssignment) {
        AssnTable.value.setSelectedRow(compData.selectedAssignment?.intakeAssignId.toString());
    }
}
async function refresh() {
    if (!compData.patientId) {
        return;
    }
    if (compData.assignmentDisplayType === AssignmentType.Active) {
        compData.activeIntakeAssignmentList = undefined;
        const resp = await ChoiceApi.Intake.RefreshActiveIntakeData(compData.patientId);
        if (!resp.success) {
            await popAlert(resp.error?.error ?? "Unable to get Report Data","Get Intake Data",ModalStyles.danger);
            return;
        }
        compData.intakeItemList = resp.success.intakeItemList
        compData.intakeAppointmentList = resp.success.intakeAppointmentList
        compData.activeIntakeAssignmentList = resp.success.intakeAssignmentList
    }
    else {
        compData.historicIntakeAssignmentList = undefined;
        const resp = await ChoiceApi.Intake.RefreshHistoricIntakeData(compData.patientId);
        if (!resp.success) {
            await popAlert(resp.error?.error ?? "Unable to get Report Data","Get Intake Data",ModalStyles.danger);
            return;
        }
        compData.intakeItemList = resp.success.intakeItemList
        compData.historicIntakeAssignmentList = resp.success.intakeAssignmentList
    }
}
async function SendIntakeSaveRequest() {
    if (!compData.patientId) {
        return;
    }
    const request = <IntakeSaveRequest>{
        patientId: compData.patientId,
        intakeAssignId: compData.saveIntakeAssignId,
        intakeSetupId: compData.saveIntakeSetupId,
        assignTypeId: compData.saveIntakeAssignType,
        appointmentId: compData.saveIntakeApptId,
        dueDate: compData.saveIntakeDueDate
    }
    const resp = await ChoiceApi.Intake.SaveIntakeRequest(request);
    if (!resp.success) {
        await popAlert(resp.error?.error ?? "Unable to get Save Intake","Save Intake Data",ModalStyles.danger);
        return;
    }
    compData.showSaveIntakeModal = false;
    refreshData();
}

async function deleteIntakeRequest(): Promise<void> {
    if (!compData.patientId) {
        return;
    }
    if (!compData.selectedAssignment) {
        await popAlert("Please select an intake assignment to delete.");
        return;
    }
    const result = await confirmDialog(`Are you sure you wish to remove Intake #${compData.selectedAssignment.intakeAssignId}?`);
    if (!result) {
        return;
    }
    const request = <IntakeDeleteRequest>{
        patientId: compData.patientId,
        intakeAssignId: compData.selectedAssignment?.intakeAssignId
    }
    const resp = await ChoiceApi.Intake.DeleteIntakeRequest(request);
    if (!resp.success) {
        await popAlert(resp.error?.error ?? "Unable to get Delete Intake Assignment","Delete Intake",ModalStyles.danger);
        return;
    }
    refreshData();
}

async function SendIntakeLinkRequest(): Promise<void> {
    if (!compData.patientId) {
        return;
    }
    if (!compData.sendIntakeToContactRecord) {
        return;
    }
    const request = <IntakeLinkCreateRequest>{
        patientId: compData.patientId,
        intakeAssignId: compData.selectedAssignment?.intakeAssignId,
        linkTransport: compData.sendIntakeToContactRecord.contactDetails,
        linkType: compData.sendIntakeToContactRecord.type,
        sendNow: true
    }
    const resp = await ChoiceApi.Intake.CreateIntakeLink(request);
    if (!resp.success) {
        await popAlert(resp.error?.error ?? "Unable to get Create Intake Link","Create Intake Link",ModalStyles.danger);
        return;
    }

    refreshData();
}
function onChangeCode(selectedContact: contactItem | void): void {
    if (!selectedContact) {
        return;
    }
    compData.sendIntakeToContactRecord = selectedContact;
}
async function fillOutInOfficeButton(): Promise<void> {
    fillOutInOffice();
}
async function fillOutInOffice(row?: IRowSelection): Promise<void> {
    if (row) {
        compData.selectedAssignment = <IntakeAssignmentActive | IntakeAssignmentHistoric>row;
    }
    compData.officeCodeRecord = undefined;
    if (!compData.patientId) {
        return;
    }
    if (!compData.selectedAssignment) {
        await popAlert("Please select an intake assignment to fill out in the office.");
        return;
    }
    if ("intakeOfficeCode" in compData.selectedAssignment) {
        if (compData.selectedAssignment?.intakeOfficeCode) {
            const request = <IntakeGetOfficeCodeRequest>{
                patientId: compData.patientId,
                intakeAssignId: compData.selectedAssignment?.intakeAssignId,
                displayInd: true
            }
            const resp = await ChoiceApi.Intake.GetOfficeCode(request);
            if (!resp.success) {
                await popAlert(resp.error?.error ?? "Could not get Office Code","Get Office Code",ModalStyles.danger);
                return;
            }
            compData.officeCodeRecord = resp.success;
        }
        else {
            const request = <IntakeGetOfficeCodeRequest>{
                patientId: compData.patientId,
                intakeAssignId: compData.selectedAssignment?.intakeAssignId,
                displayInd: false
            }
            const resp = await ChoiceApi.Intake.GetOfficeCode(request);
            if (!resp.success) {
                await popAlert(resp.error?.error ?? "Could not get Office Code","Get Office Code",ModalStyles.danger);
                return;
            }
            compData.officeCodeRecord = resp.success;
        }
    }
    PostOfficeCode();
    compData.showOfficeCodeDialog = true;
}

async function PostOfficeCode(): Promise<void> {
    compData.loadingPostOfficeCode = endPointState.loading;
    if (!compData.officeCodeRecord) {
        return;
    }
    if (!compData.officeCodeRecord.officeCode) {
        return;
    }
    const request = <IntakeOfficeCodePostRequest>{
        patientId: compData.patientId,
        intakeAssignId: compData.selectedAssignment?.intakeAssignId,
        officeCode: compData.officeCodeRecord.officeCode
    };
    let resp = await ChoiceApi.Intake.PostOfficeCode(request);
    if (!resp.success) {
        await popAlert(resp.error?.error ?? "Unable to Post Office Code","Get Office Code",ModalStyles.danger);
        compData.loadingPostOfficeCode = endPointState.error;
        return;
    }
    compData.loadingPostOfficeCode = endPointState.success;
    refreshData();
}

function onAssnCellClick(colData: ICol, row: IRowSelection, _rowType: TableRowType, _evt: MouseEvent): void {
    compData.selectedAssignment = <IntakeAssignmentActive | IntakeAssignmentHistoric>row;
}

function openSelectedIntakeDetails() {
    compData.showAssignmentDetailsDialog = true;
}
const isAssignmentTypeActive = computed<boolean>(() => {
    return compData.assignmentDisplayType === AssignmentType.Active;
});

const tableColumnsAppt = ref<ICol[]>([
    { DataField: 'appointmentStartTime', Name: 'Date', HideFilter: true },
    { DataField: 'appointmentType', Name: 'Type', HideFilter: true },
    { DataField: 'scheduledWith', Name: 'Scheduled With', HideFilter: true },
    { DataField: 'serviceLocation', Name: 'Location', HideFilter: true },
    { DataField: 'intakeName', Name: 'Intake Name', HideFilter: true },
    { DataField: 'intakeStatus', Name: 'Status', HideFilter: true }
]);

const tableColumnsAssignActive = ref<ICol[]>([
    { DataField: 'intakeAssignId', Name: 'ID', HideFilter: true },
    { DataField: 'intakeOfficeCode', Name: 'Fill Out In Office', ExtraData: { isOffCode: true }, HideFilter: true, Alignment: AlignmentType.Center },
    { DataField: 'intakeLinkInd', Name: 'Link Sent', ExtraData: { isLinkInd: true }, HideFilter: true, Alignment: AlignmentType.Center },
    { DataField: 'name', Name: 'Intake Name', HideFilter: true },
    { DataField: 'assignTypeDescription', Name: 'Type', HideFilter: true },
    { DataField: 'dueDate', Name: 'Due Date', HideFilter: true },
    { DataField: 'intakeStatusDescription', Name: 'Status', HideFilter: true },
    { DataField: 'intakeProgress', Name: 'Intake', HideFilter: true },
    { DataField: 'requiredProgress', Name: 'Required', HideFilter: true },
    { DataField: 'lastUpdatedDate', Name: 'Updated', HideFilter: true },
    { DataField: '', Name: 'Details', ExtraData: { isDetails: true }, HideFilter: true, Alignment: AlignmentType.Center }
]);
const tableColumnsAssignHistoric = ref<ICol[]>([
    { DataField: 'intakeAssignId', Name: 'ID', HideFilter: true },
    { DataField: 'intakeLinkInd', Name: 'Link Sent', ExtraData: { isLinkInd: true }, HideFilter: true, Alignment: AlignmentType.Center },
    { DataField: 'name', Name: 'Intake Name', HideFilter: true },
    { DataField: 'assignTypeDescription', Name: 'Type', HideFilter: true },
    { DataField: 'dueDate', Name: 'Due Date', HideFilter: true },
    { DataField: 'intakeStatusDescription', Name: 'Status', HideFilter: true },
    { DataField: 'intakeProgress', Name: 'Intake', HideFilter: true },
    { DataField: 'requiredProgress', Name: 'Required', HideFilter: true },
    { DataField: 'archiveDate', Name: 'Archived', HideFilter: true },
    { DataField: '', Name: 'Details', ExtraData: { isDetails: true }, HideFilter: true, Alignment: AlignmentType.Center }
]);

async function openSaveIntakeModalEdit(): Promise<void> {
    if (!compData.selectedAssignment) {
        await popAlert("Please select an intake assignment to edit.");
        return;
    }
    compData.disableSaveIntakeCombo = true;
    compData.saveIntakeSetupId = compData.selectedAssignment.intakeSetupId;
    compData.saveIntakeAssignId = compData.selectedAssignment.intakeAssignId;
    if (compData.selectedAssignment.assignTypeId == saveIntakeAssignType.Appointment) {
        compData.saveIntakeAssignType = saveIntakeAssignType.Appointment;
        compData.saveIntakeApptId = compData.selectedAssignment.appointmentId;
        compData.saveIntakeDueDate = undefined;
    }
    if (compData.selectedAssignment.assignTypeId == saveIntakeAssignType.DueDate) {
        compData.saveIntakeAssignType = saveIntakeAssignType.DueDate;
        compData.saveIntakeApptId = undefined;
        if (!compData.selectedAssignment.dueDate) {
            return;
        }
        compData.saveIntakeDueDate = DateUtils.toNoTzDate(new Date(compData.selectedAssignment.dueDate));
    }
    openSaveIntakeModal();
    return;
}

async function openSaveIntakeModalNew(): Promise<void> {
    //Reset window state
    compData.disableSaveIntakeCombo = false;
    compData.saveIntakeAssignType = saveIntakeAssignType.Appointment;
    compData.saveIntakeAssignId = undefined;
    compData.saveIntakeApptId = undefined;
    compData.saveIntakeDueDate = undefined;
    compData.saveIntakeSetupId = undefined;
    openSaveIntakeModal();
    return;
}
async function openAudit(): Promise<void> {
    if (!compData.patientId) {
        return;
    }
    if (!compData.selectedAssignment) {
        await popAlert("Please select an intake assignment to audit.");
        return;
    }
    const auditURL = loggingURL(compData.patientId, compData.selectedAssignment?.intakeAssignId);
    openWindowFromVue("Logging", auditURL, { noTimestamp: "N" });
}
function clearSelection() {
    compData.selectedAssignment = undefined;
    AssnTable.value.clearSelectedRow();
    return;
}

async function openSaveIntakeModal(): Promise<void> {
    const resp = await ChoiceApi.Intake.GetIntakeEdit(compData.patientId ?? -1, compData.selectedAssignment?.intakeAssignId);
    if(!resp.success){
        await popAlert(resp.error?.error ?? "Unable to get Appointment Data","Get Appointment Data",ModalStyles.danger);
    }
    compData.intakeAppointmentDescription = resp.success;
    compData.showSaveIntakeModal = true;
    return;
}
async function openSendIntakeModal(): Promise<void> {
    if (!compData.selectedAssignment) {
        await popAlert("Please select an intake assignment to send the link for.");
        return;
    }
    if (0 == compData.toContactList?.length || !compData.toContactList) {
        await popAlert("Cannot send a link to an empty number/email.  Please setup this number in Patient Demographics.");
        return;
    }
    compData.sendIntaketoContactType = compData.toContactList[0].type;
    compData.sendIntakeToContactRecord = compData.toContactList[0];
    compData.showSendIntakeModal = true;
    return;
}
async function openMergeModal(): Promise<void> {
    if (!compData.selectedAssignment) {
        await popAlert("Please select an intake assignment to merge with.");
        return;
    }
    if (!compData.patientId)
        return;
    const mergeURL = mergeIntakeURL(compData.patientId, compData.selectedAssignment?.intakeAssignId);
    openWindowFromVue("Logging", mergeURL, { noTimestamp: "N" });
    return;
}

function progressColor(input: number): string {

    if (input <= 25) return "bg-danger"; // Red
    if (input <= 75) return "bg-warning"; // Yellow
    return "bg-success"; // Green
}
</script>
<template>
    <ToastContainer :toasts="toastsArray" :defaultHideDelayMilliseconds="10000" :warnIfMessageLibNotAvailable="false" />
    <ResizeMonitor @resize="windowResize" />
    <WindowHeaderFooter :headerHeight="3" :footerHeight="4.5">
        <template v-slot:WindowHeader>
            <div class="row mb-1">
                <div class="isalus-page-title-large pt-1 pb-1 col">Intake Assignment
                </div>
                <PatientHeader class="col pt-2 pb-2" :displayContext="PortalType.Scheduler" :legacyPage="false"
                    :patientEntityId="compData.patientData?.patientEntityId" />
            </div>
        </template>
        <template v-slot:WindowBody>
            <div>
                <LoadingSection class="label-isalus-small" :isLoading="!compData.intakeAppointmentList"
                    loadingMsg="Getting Appointments">
                    <div class="row mb-3">
                        <div class="col text-center isalus-section-title">
                            Next 5 Appointments
                        </div>
                    </div>
                    <ChoiceTable :data="compData.intakeAppointmentList" :columns="tableColumnsAppt" :allowSorting="true"
                        :key-value-data-member="'intakeAssignId'" />
                </LoadingSection>
                <div class="row mb-1">
                    <div class="col text-start">
                        <RadioButtonInput :value="AssignmentType.Active" v-model="compData.assignmentDisplayType"
                            :layout="LayoutOrientation.Horz" @update:model-value="onChangeAssignment()">
                            <div class="label-isalus-small">Active</div>
                        </RadioButtonInput>
                        <RadioButtonInput :value="AssignmentType.Historical" v-model="compData.assignmentDisplayType"
                            :layout="LayoutOrientation.Horz" @update:model-value="onChangeAssignment()">
                            <div class="label-isalus-small">Historical</div>
                        </RadioButtonInput>
                    </div>
                    <div class="col text-center isalus-section-title label-isalus-small">
                        Intakes Assigned
                    </div>
                    <div class="col text-end">
                        <div class="btn-group" role="toolbar" aria-label="Cool ToolBar Collection">

                            <ActionButtonV2 :action="ActionButtonStyle.Refresh" :text="'Refresh'"
                                :size="ButtonDisplaySize.Small" :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                                :performAction="refreshData" />
                        </div>
                    </div>
                </div>
                <LoadingSection v-show="isAssignmentTypeActive" class="label-isalus-small"
                    :isLoading="!compData.activeIntakeAssignmentList">
                    <ChoiceTable ref="AssnTable" :data="compData.activeIntakeAssignmentList"
                        :columns="tableColumnsAssignActive" :itemsPerPage="5"
                        :showPaginationButtonsBelowChoiceTable="true" :allowSorting="true"
                        :rowHighlight="TableRowHighlight.onSingleClick" :key-value-data-member="'intakeAssignId'"
                        @cellclick="onAssnCellClick">
                        <template #cell="{ col, row, value }">
                            <template v-if="'Fill Out In Office' === col.Name">
                                <div v-if="value">
                                    <Icon :icon="IconTypes.MobileScreen" :displayStyle="DisplayStyle.Primary"
                                        @click="fillOutInOffice(row)" :hoverCursor="HoverCursorTypes.Pointer"
                                        :class="'fa-lg'" :title="value" />
                                </div>
                                <div v-else>
                                </div>
                            </template>
                            <template v-if="'Link Sent' === col.Name">
                                <div v-if="'Y' === value">
                                    <Icon :icon="IconTypes.Send" :displayStyle="DisplayStyle.Primary"
                                        @click="openSendIntakeModal()" :hoverCursor="HoverCursorTypes.Pointer"
                                        :class="'fa-lg'" title="Intake link has been sent to this patient" />
                                </div>
                                <div v-else>
                                </div>
                            </template>
                            <template v-if="'Details' === col.Name">
                                <Icon :icon="IconTypes.RegularClipboard" :displayStyle="DisplayStyle.Primary"
                                    @click="openSelectedIntakeDetails()" :hoverCursor="HoverCursorTypes.Pointer"
                                    :class="'fa-lg'" title="Display additional intake details" />
                            </template>
                            <template v-if="'Intake' === col.Name">
                                <ProgressBarIcon :percentage="row.intakeProgress" />
                            </template>
                            <template v-if="'Required' === col.Name">
                                <ProgressBarIcon :percentage="row.requiredProgress" />
                            </template>
                        </template>
                    </ChoiceTable>
                </LoadingSection>
                <LoadingSection v-show="!isAssignmentTypeActive" class="label-isalus-small"
                    :isLoading="!compData.historicIntakeAssignmentList">
                    <ChoiceTable ref="AssnTable" :data="compData.historicIntakeAssignmentList"
                        :columns="tableColumnsAssignHistoric" :itemsPerPage="5"
                        :showPaginationButtonsBelowChoiceTable="true" :allowSorting="true"
                        :rowHighlight="TableRowHighlight.onSingleClick" :key-value-data-member="'intakeAssignId'"
                        @cellclick="onAssnCellClick">
                        <template #cell="{ col, row, value }">
                            <template v-if="'Link Sent' === col.Name">
                                <div v-if="'Y' === value">
                                    <Icon :icon="IconTypes.Send" :displayStyle="DisplayStyle.Primary"
                                        :hoverCursor="HoverCursorTypes.Pointer" :class="'fa-lg'"
                                        @click="openSendIntakeModal()"
                                        title="Intake link has been sent to this patient" />
                                </div>
                                <div v-else>

                                </div>
                            </template>
                            <template v-if="'Details' === col.Name">
                                <Icon :icon="IconTypes.Clipboard" :displayStyle="DisplayStyle.Primary"
                                    :hoverCursor="HoverCursorTypes.Pointer" :class="'fa-lg'"
                                    @click="openSelectedIntakeDetails()" title="Display additional intake details" />
                            </template>
                            <template v-if="'Intake' === col.Name">
                                <ProgressBarIcon :percentage="row.intakeProgress" />
                            </template>
                            <template v-if="'Required' === col.Name">
                                <ProgressBarIcon :percentage="row.requiredProgress" />
                            </template>
                        </template>
                    </ChoiceTable>
                </LoadingSection>
            </div>
        </template>
        <template v-slot:WindowFooter>
            <div class="row mt-2">
                <div class="col text-start">
                    <div class="btn-group" role="toolbar" aria-label="Cool ToolBar Collection">
                        <ActionButtonV2 :action="ActionButtonStyle.New" :text="'New'" :size="ButtonDisplaySize.Small"
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary" :performAction="openSaveIntakeModalNew" />
                        <ActionButtonV2 :action="ActionButtonStyle.Edit" :text="'Edit'" :size="ButtonDisplaySize.Small"
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                            :performAction="openSaveIntakeModalEdit" />
                        <ActionButtonV2 :action="ActionButtonStyle.Audit" :text="'Audit'"
                            :size="ButtonDisplaySize.Small" :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                            :performAction="openAudit" />
                        <ActionButtonV2 :action="ActionButtonStyle.Delete" :text="'Delete'"
                            :size="ButtonDisplaySize.Small" :displayStyle="ButtonDisplayStyle.OutlineDanger"
                            :performAction="deleteIntakeRequest" />
                    </div>
                </div>
                <div class="col text-end">
                    <div class="btn-group" role="toolbar" aria-label="Cool ToolBar Collection">
                        <ActionButtonV2 :action="ActionButtonStyle.Send" :text="'Send Intake Link'"
                            :size="ButtonDisplaySize.Small" :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                            :performAction="openSendIntakeModal" />
                        <ActionButtonV2 :action="ActionButtonStyle.Link" :text="'Fill Out In Office'"
                            :size="ButtonDisplaySize.Small" :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                            :performAction="fillOutInOfficeButton"
                            :disabled="AssignmentType.Historical === compData.assignmentDisplayType" />
                        <ActionButtonV2 :action="ActionButtonStyle.CreditCard" :text="'Merge Response'"
                            :size="ButtonDisplaySize.Small" :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                            :performAction="openMergeModal" />
                    </div>
                </div>
            </div>
        </template>
    </WindowHeaderFooter>
    <ModalDialog v-model:show="compData.showSaveIntakeModal" :closeOnClickOutside=true>
        <template #title>New Intake Assignment</template>
        <div class="row mt-2">
            <div class="col-3 container">
                <div class="row">
                    <div class="col-1 text-start">
                    </div>
                    <div class="col-9  mt-1 text-start label-isalus-small">
                        <div>
                            Intake
                            <span class="red-text">
                                *
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-9 text-start">
                <Combo :optionList="IntakeType ?? []" optionValue="intakeSetupId" optionDescription="intakeName"
                    optionHeader="status" :includeHeader="true" :includeBlank="false"
                    v-model:selected="compData.saveIntakeSetupId" :disabled="compData.disableSaveIntakeCombo" />
            </div>
        </div>
        <div class="row mt-2">
            <div class="col-3 container">
                <div class="row">
                    <div class="col-1 text-start">
                        <RadioButtonInput :value="saveIntakeAssignType.Appointment"
                            v-model="compData.saveIntakeAssignType" :layout="LayoutOrientation.Vert">
                        </RadioButtonInput>
                    </div>
                    <div class="label-isalus-small col-9 mt-1 text-start">Appointment <span
                            v-show="compData.saveIntakeAssignType === saveIntakeAssignType.Appointment"
                            class="red-text">*</span>
                    </div>
                </div>
            </div>
            <div class="col-9 text-start">
                <Combo :optionList="compData.intakeAppointmentDescription ?? []" optionValue="appointmentId"
                    optionDescription="appointmentDescription" :includeHeader="false" :includeBlank="true"
                    :disabled="compData.saveIntakeAssignType !== saveIntakeAssignType.Appointment"
                    v-model:selected="compData.saveIntakeApptId" />
            </div>
        </div>
        <div class="row mt-2">
            <div class="col-3 container">
                <div class="row">
                    <div class="col-1 text-start">
                        <RadioButtonInput :value="saveIntakeAssignType.DueDate" v-model="compData.saveIntakeAssignType"
                            :layout="LayoutOrientation.Vert">
                        </RadioButtonInput>
                    </div>
                    <div class="col-9 mt-1 text-start label-isalus-small">Due Date <span
                            v-show="compData.saveIntakeAssignType === saveIntakeAssignType.DueDate"
                            class="red-text">*</span>
                    </div>
                </div>
            </div>
            <div class="col-3 text-start label-isalus-small">
                <DatePickerNoTZ v-model:value="compData.saveIntakeDueDate" :clearable="false"
                    :inputClassName="'small_date'" :textExtraClass="'label-isalus-small'"
                    :disabled="compData.saveIntakeAssignType !== saveIntakeAssignType.DueDate"
                    :display-format="DateDisplayFormat.NumericDate" />
            </div>
            <div class="col-6">

            </div>
        </div>
        <template #footer>
            <ActionButtonV2 :action="ActionButtonStyle.Save" :text="'Save'" :size="ButtonDisplaySize.Small"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary" :performAction="SendIntakeSaveRequest"
                :disabled="!validIntakeSave" />
        </template>
    </ModalDialog>
    <ModalDialog v-model:show="compData.showSendIntakeModal" :closeOnClickOutside=true>
        <template #title>Send Intake Link</template>
        <div class="row mt-2">
            <div class="col-3 container">
                <div class="row">
                    <div class="col-1 text-start">
                    </div>
                    <div class="col-9  mt-1 text-start label-isalus-small">
                        <div>
                            To Contact
                            <span class="red-text">
                                *
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-9 text-start">
                <Combo :optionList="compData.toContactList ?? []" :includeHeader="false" :includeBlank="false"
                    :optionValue="'type'" :optionDescription="'contactDetails'"
                    v-model:selected="compData.sendIntaketoContactType" @onChange="onChangeCode" />
            </div>
        </div>
        <template #footer>
            <ActionButtonV2 :action="ActionButtonStyle.Send" :text="'Send'" :size="ButtonDisplaySize.Small"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary" :performAction="SendIntakeLinkRequest" />
        </template>
    </ModalDialog>
    <GeneralDialog v-model:show="compData.showOfficeCodeDialog" 
        :closeModalOnClickOutside="false"
        :allowDragging="false"
        :width="dialogWidth">        
        <template #title>
            <div class="isalus-section-title">Office Code</div>
        </template>
        <div class="isalus-page-title">
            {{ compData.officeCodeRecord?.officeCode }}<br>
        </div>
        {{ compData.officeCodeRecord?.lastName }}<br>
        {{ compData.officeCodeRecord?.birthDate }}
        <div class="row">
            <div class="col">Post status:</div>
            <div class="col text-end">
                <LoadingSectionV2 class="label-isalus-small" :isLoading="compData.loadingPostOfficeCode"
                    loadingMsg="Posting Code">
                    <Icon :icon="IconTypes.CheckMark" :displayStyle="DisplayStyle.Success"
                        :hoverCursor="HoverCursorTypes.Pointer" :class="'fa-lg'"
                        title="Display additional intake details" />
                    <template #error>
                        <AlertSection :displayStyle="DisplayStyle.Danger">
                            Could not Post Code
                        </AlertSection>
                    </template>
                </LoadingSectionV2>
            </div>
        </div>
        <template #footer>
            <ActionButtonV2 :action="ActionButtonStyle.Post" :text="'Post'" :size="ButtonDisplaySize.Small"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary" :performAction="PostOfficeCode" />
        </template>
    </GeneralDialog>
    <GeneralDialog v-model:show="compData.showAssignmentDetailsDialog" 
        :closeOnClickOutside="true"
        :allowDragging="false"
        :width="dialogWidth">
        <template #title>
            <div class="isalus-section-title">Intake Details</div>
        </template>
        <div class="row">
            <div class="col label-isalus-small text-start">
                Device ID:
            </div>
            <div class="col label-isalus-small text-start">
                {{ compData.selectedAssignment?.deviceId }}
            </div>
        </div>
        <div class="row">
            <div class="col label-isalus-small text-start">
                User:
            </div>
            <div class="col label-isalus-small text-start">
                {{ compData.selectedAssignment?.userId }}
            </div>
        </div>
        <div class="row">
            <div class="col label-isalus-small text-start">
                Created:
            </div>
            <div class="col label-isalus-small text-start">
                {{ compData.selectedAssignment?.createdDate }}
            </div>
        </div>
        <div class="row">
            <div class="col label-isalus-small text-start">
                Copay:
            </div>
            <div class="col label-isalus-small text-start">
                {{ compData.selectedAssignment?.copayAmount }}
            </div>
        </div>
        <div class="row">
            <div class="col label-isalus-small text-start">
                Paid:
            </div>
            <div class="col label-isalus-small text-start">
                {{ compData.selectedAssignment?.copayPaid }}
            </div>
        </div>
        <div class="row">
            <div class="col label-isalus-small text-start">
                Eligibility:
            </div>
            <div class="col label-isalus-small text-start">
                {{ compData.selectedAssignment?.eligibility }}
            </div>
        </div>
    </GeneralDialog>
</template>